<template>
  <div ref="container" class="three-container" :class="{ measuring }" :style="containerStyle">
    <canvas ref="canvas"></canvas>
    <div v-if="loading" class="loading-overlay">
      <div class="loading-spinner"></div>
      <div class="loading-text">{{ loadingText }}</div>
    </div>
  </div>
</template>

<script lang="ts">
import Vue from 'vue'
import * as THREE from 'three'
import { SceneManager } from '../core/SceneManager'
import { loadModel, clearCache } from '../core/ModelLoader'
import { MeasurementTool } from '../core/MeasurementTool'
import { AnnotationTool } from '../core/AnnotationTool'
import { splitConnectedComponents } from '../core/MeshSplitter'
import type { SceneOptions, SceneReadyPayload, ModelFormat, ModelLoadResult, ClipConfig } from '../core/types'

export default Vue.extend({
  name: 'ThreeRenderer',

  props: {
    width: { type: [Number, String], default: '100%' },
    height: { type: [Number, String], default: '100%' },
    backgroundColor: { type: Number, default: 0x1a1a2e },
    cameraPosition: { type: Object, default: () => ({ x: 5, y: 5, z: 5 }) },
    cameraFov: { type: Number, default: 45 },
    cameraNear: { type: Number, default: 0.1 },
    cameraFar: { type: Number, default: 1000 },
    autoRender: { type: Boolean, default: true },
    enableOrbit: { type: Boolean, default: true },
    autoCenter: { type: Boolean, default: true },
    autoScale: { type: Boolean, default: true },
    measuring: { type: Boolean, default: false },
    annotating: { type: Boolean, default: false },
  },

  data() {
    return {
      sceneManager: null as SceneManager | null,
      resizeHandler: null as (() => void) | null,
      loading: false,
      loadingText: '',
      models: [] as THREE.Group[],
      measureTool: null as MeasurementTool | null,
      annotationTool: null as AnnotationTool | null,
      isRightDragging: false,
      dragPlane: new THREE.Plane(),
      dragGroup: null as THREE.Group | null,
      dragOffset: new THREE.Vector3(),
      dragGroupLocalPos: new THREE.Vector3(),
      initialModelPositions: new Map<THREE.Group, THREE.Vector3>(),
    }
  },

  computed: {
    containerStyle() {
      return {
        width: typeof this.width === 'number' ? this.width + 'px' : this.width,
        height: typeof this.height === 'number' ? this.height + 'px' : this.height,
      }
    },
  },

  watch: {
    measuring(val: boolean) {
      if (this.sceneManager?.controls) {
        this.sceneManager.controls.enabled = !val && !this.annotating
      }
    },
    annotating(val: boolean) {
      if (this.sceneManager?.controls) {
        this.sceneManager.controls.enabled = !val && !this.measuring
      }
    },
  },

  mounted() {
    const canvas = this.$refs.canvas as HTMLCanvasElement
    if (!canvas) return

    const options: SceneOptions = {
      canvas,
      backgroundColor: this.backgroundColor,
      cameraPosition: this.cameraPosition as { x: number; y: number; z: number },
      cameraFov: this.cameraFov,
      cameraNear: this.cameraNear,
      cameraFar: this.cameraFar,
      enableOrbit: this.enableOrbit,
    }

    this.sceneManager = new SceneManager(options)

    if (this.autoRender) {
      this.sceneManager.start()
    }

    this.measureTool = new MeasurementTool(this.sceneManager.scene, this.sceneManager.camera)
    this.annotationTool = new AnnotationTool(this.sceneManager.scene)

    canvas.addEventListener('pointerdown', this.onCanvasPointerDown)
    canvas.addEventListener('pointermove', this.onCanvasPointerMove)
    canvas.addEventListener('pointerup', this.onCanvasPointerUp)
    canvas.addEventListener('contextmenu', e => e.preventDefault())

    this.$emit('scene-ready', {
      scene: this.sceneManager.scene,
      camera: this.sceneManager.camera,
      renderer: this.sceneManager.renderer,
      manager: this.sceneManager,
    } as SceneReadyPayload)

    this.resizeHandler = () => this.sceneManager?.resize()
    window.addEventListener('resize', this.resizeHandler)
  },

  beforeDestroy() {
    const canvas = this.$refs.canvas as HTMLCanvasElement
    if (canvas) {
      canvas.removeEventListener('pointerdown', this.onCanvasPointerDown)
      canvas.removeEventListener('pointermove', this.onCanvasPointerMove)
      canvas.removeEventListener('pointerup', this.onCanvasPointerUp)
    }
    if (this.resizeHandler) {
      window.removeEventListener('resize', this.resizeHandler)
    }
    this.sceneManager?.dispose()
    this.measureTool?.dispose()
    this.annotationTool?.dispose()
    this.sceneManager = null
  },

  methods: {
    getSceneManager(): SceneManager | null {
      return this.sceneManager
    },

    getScene(): THREE.Scene | null {
      return this.sceneManager?.scene || null
    },

    getModelGroups(): THREE.Group[] {
      return this.models
    },

    getModelCount(): number {
      return this.models.length
    },

    applyClip(config: ClipConfig) {
      if (!this.sceneManager) return
      for (const group of this.models) {
        this.sceneManager.clipping.apply(config, group)
      }
    },

    resetView() {
      for (const [group, pos] of this.initialModelPositions) {
        group.position.copy(pos)
      }
      this.sceneManager?.resetView()
    },

    addModel(url: string, format?: ModelFormat): Promise<ModelLoadResult> {
      this.loading = true
      this.loadingText = this.$t('loading.modelLoading') as string

      return loadModel({
        url,
        format: format || undefined,
        autoCenter: this.autoCenter,
        autoScale: this.autoScale,
        onProgress: (pct: number) => {
          this.loadingText = this.$t('loading.loadingPercent', { percent: Math.round(pct * 100) }) as string
        },
      })
        .then((result: ModelLoadResult) => {
          this.models.push(result.group)
          this.initialModelPositions.set(result.group, result.group.position.clone())
          this.sceneManager?.add(result.group)
          this.sceneManager?.fitToObjects(this.models)
          if (this.models.length === 1) {
            this.sceneManager?.saveCurrentViewAsReset()
          }
          this.$emit('model-loaded', result)
          return result
        })
    .catch((err: Error) => {
      console.error('addModel error', err)
      this.$emit('model-error', err)
      throw err
    })
        .finally(() => {
          this.loading = false
        })
    },

    removeModel(index: number) {
      const group = this.models[index]
      if (group) {
        this.sceneManager?.remove(group)
        this.models.splice(index, 1)
        this.initialModelPositions.delete(group)
      }
    },

    setModelVisible(index: number, visible: boolean) {
      const group = this.models[index]
      if (group) group.visible = visible
    },

    clearModels() {
      clearCache()
      for (const group of this.models) {
        this.sceneManager?.remove(group)
      }
      this.models = []
      this.initialModelPositions.clear()
    },

    syncInitialPositions() {
      this.initialModelPositions.clear()
      for (const group of this.models) {
        this.initialModelPositions.set(group, group.position.clone())
      }
    },

    focusModel(index: number) {
      const group = this.models[index]
      if (!group || !this.sceneManager) return
      const box = new THREE.Box3().setFromObject(group)
      if (box.isEmpty()) return
      const center = box.getCenter(new THREE.Vector3())
      const size = box.getSize(new THREE.Vector3())
      const maxDim = Math.max(size.x, size.y, size.z)
      const sm = this.sceneManager
      if (sm.controls) {
        sm.controls.target.copy(center)
        const dist = maxDim > 0 ? maxDim * 1.5 + 2 : 5
        sm.camera.position.set(center.x + dist * 0.6, center.y + dist * 0.6, center.z + dist * 0.6)
        sm.controls.update()
      }
    },

    findModelGroup(obj: THREE.Object3D | null): THREE.Group | null {
      let cur: THREE.Object3D | null = obj
      while (cur) {
        if (this.models.includes(cur as THREE.Group)) {
          return cur as THREE.Group
        }
        cur = cur.parent
      }
      return null
    },

    raycastFromEvent(e: PointerEvent): THREE.Intersection | null {
      const canvas = this.$refs.canvas as HTMLCanvasElement
      if (!canvas || !this.sceneManager) return null
      const rect = canvas.getBoundingClientRect()
      const x = ((e.clientX - rect.left) / rect.width) * 2 - 1
      const y = -((e.clientY - rect.top) / rect.height) * 2 + 1
      const raycaster = new THREE.Raycaster()
      raycaster.setFromCamera(new THREE.Vector2(x, y), this.sceneManager.camera)
      const meshes: THREE.Mesh[] = []
      for (const group of this.models) {
        group.traverse(c => { if (c instanceof THREE.Mesh) meshes.push(c) })
      }
      const hits = raycaster.intersectObjects(meshes, false)
      return hits.length > 0 ? hits[0] : null
    },

    onCanvasPointerDown(e: PointerEvent) {
      if (this.measuring || this.annotating) {
        const canvas = this.$refs.canvas as HTMLCanvasElement
        if (!canvas || !this.sceneManager) return
        const rect = canvas.getBoundingClientRect()
        const x = ((e.clientX - rect.left) / rect.width) * 2 - 1
        const y = -((e.clientY - rect.top) / rect.height) * 2 + 1
        const raycaster = new THREE.Raycaster()
        raycaster.setFromCamera(new THREE.Vector2(x, y), this.sceneManager.camera)
        const meshes: THREE.Mesh[] = []
        for (const group of this.models) {
          group.traverse(c => { if (c instanceof THREE.Mesh) meshes.push(c) })
        }
        const hits = raycaster.intersectObjects(meshes, false)
        if (hits.length > 0) {
          const pt = hits[0].point
          let obj: THREE.Object3D | null = hits[0].object
          let invScale = 1
          while (obj) {
            if (this.models.includes(obj as THREE.Group)) {
              invScale = (obj as THREE.Group).userData.inverseScale || 1
              break
            }
            obj = obj.parent
          }
          if (this.measuring && this.measureTool) {
            this.measureTool.addPoint(pt, invScale)
            this.$emit('measure-point', pt)
          }
          if (this.annotating && this.annotationTool) {
            this.$emit('annotation-add', pt)
          }
        }
        return
      }

      if (e.button === 2) {
        const hit = this.raycastFromEvent(e)
        const group = hit ? this.findModelGroup(hit.object) : null
        if (group && this.sceneManager?.controls) {
          this.isRightDragging = true
          this.dragGroup = group

          const camera = this.sceneManager.camera
          const camDir = new THREE.Vector3()
          camera.getWorldDirection(camDir)
          this.dragPlane.setFromNormalAndCoplanarPoint(camDir.clone().negate(), hit!.point)

          this.dragOffset.copy(hit!.point).sub(group.position)
          this.dragGroupLocalPos.copy(group.position)

          this.sceneManager.controls.enabled = false

          const canvas = this.$refs.canvas as HTMLCanvasElement
          if (canvas) canvas.style.cursor = 'grabbing'
        }
      }
    },

    onCanvasPointerMove(e: PointerEvent) {
      if (!this.isRightDragging || !this.dragGroup || !this.sceneManager) return

      const canvas = this.$refs.canvas as HTMLCanvasElement
      if (!canvas) return
      const rect = canvas.getBoundingClientRect()
      const x = ((e.clientX - rect.left) / rect.width) * 2 - 1
      const y = -((e.clientY - rect.top) / rect.height) * 2 + 1
      const raycaster = new THREE.Raycaster()
      raycaster.setFromCamera(new THREE.Vector2(x, y), this.sceneManager.camera)

      const intersection = new THREE.Vector3()
      if (raycaster.ray.intersectPlane(this.dragPlane, intersection)) {
        const newPos = intersection.sub(this.dragOffset)
        this.dragGroup.position.copy(newPos)
      }
    },

    onCanvasPointerUp(e: PointerEvent) {
      if (e.button === 2 && this.isRightDragging) {
        if (this.dragGroup && this.sceneManager?.controls) {
          this.sceneManager.controls.enabled = true

          const box = new THREE.Box3().setFromObject(this.dragGroup)
          const center = box.getCenter(new THREE.Vector3())
          this.sceneManager.controls.target.copy(center)
          this.sceneManager.controls.update()

          this.$emit('model-moved', {
            group: this.dragGroup,
            position: this.dragGroup.position.clone(),
          })
        }

        this.isRightDragging = false
        this.dragGroup = null

        const canvas = this.$refs.canvas as HTMLCanvasElement
        if (canvas) canvas.style.cursor = ''
        return
      }

      if (this.measuring || this.annotating) return
      this.setOrbitTarget(e)
    },

    setOrbitTarget(e: PointerEvent) {
      const canvas = this.$refs.canvas as HTMLCanvasElement
      if (!canvas || !this.sceneManager) return
      const rect = canvas.getBoundingClientRect()
      const x = ((e.clientX - rect.left) / rect.width) * 2 - 1
      const y = -((e.clientY - rect.top) / rect.height) * 2 + 1
      const raycaster = new THREE.Raycaster()
      raycaster.setFromCamera(new THREE.Vector2(x, y), this.sceneManager.camera)
      const meshes: THREE.Mesh[] = []
      for (const group of this.models) {
        group.traverse(c => { if (c instanceof THREE.Mesh) meshes.push(c) })
      }
      const hits = raycaster.intersectObjects(meshes, false)
      if (hits.length > 0 && this.sceneManager.controls) {
        const ctrl = this.sceneManager.controls
        ctrl.enableDamping = false
        ctrl.target.copy(hits[0].point)
        ctrl.update()
        ctrl.enableDamping = true
      }
    },

    clearMeasurements() {
      this.measureTool?.clear()
    },

    splitModel(index: number): { count: number; names: string[]; groups: THREE.Group[] } | null {
      const group = this.models[index]
      if (!group || !this.sceneManager) return null

      const result = splitConnectedComponents(group)
      if (result.componentCount <= 1) return null

      const origPos = group.position.clone()
      const origQuat = group.quaternion.clone()
      const origScale = group.scale.clone()

      this.sceneManager.remove(group)
      this.models.splice(index, 1)
      this.initialModelPositions.delete(group)

      const insertAt = index
      for (let i = 0; i < result.groups.length; i++) {
        const g = result.groups[i]
        g.position.copy(origPos)
        g.quaternion.copy(origQuat)
        g.scale.copy(origScale)
        this.sceneManager.add(g)
        this.models.splice(insertAt + i, 0, g)
        this.initialModelPositions.set(g, origPos.clone())
      }

      this.sceneManager.fitToObjects(this.models)

      return {
        count: result.componentCount,
        names: result.groups.map((_, i) => `${this.$t('model.part')}${i + 1}`),
        groups: result.groups,
      }
    },

    addAnnotationAt(position: THREE.Vector3, text: string) {
      return this.annotationTool?.addAnnotation(position, text) || null
    },

    updateAnnotation(id: string, text: string) {
      this.annotationTool?.updateText(id, text)
    },

    removeAnnotation(id: string) {
      this.annotationTool?.removeAnnotation(id)
    },

    clearAnnotations() {
      this.annotationTool?.clear()
    },

    getAnnotations() {
      return this.annotationTool?.annotations || []
    },
  },
})
</script>

<style scoped>
.three-container {
  position: relative;
  overflow: hidden;
}
.three-container canvas {
  display: block;
  width: 100%;
  height: 100%;
  touch-action: none;
  user-select: none;
}
.three-container.measuring canvas {
  cursor: crosshair;
}
.loading-overlay {
  position: absolute;
  inset: 0;
  display: flex;
  flex-direction: column;
  align-items: center;
  justify-content: center;
  background: rgba(15, 15, 26, 0.85);
  color: #e0e0e0;
  font-size: 14px;
  z-index: 10;
}
.loading-spinner {
  width: 36px;
  height: 36px;
  border: 3px solid #333;
  border-top-color: #00aaff;
  border-radius: 50%;
  animation: spin 0.8s linear infinite;
  margin-bottom: 12px;
}
@keyframes spin {
  to { transform: rotate(360deg); }
}
</style>
